import { useState, useEffect, useCallback } from 'react';
import { apiFetch, ApiError } from '../lib/api.js';
import { useAuth } from '../lib/auth-context.js';
import { useOrg } from '../lib/org-context.js';
import { useToast } from '../components/Toast.js';
import { ErrorBoundary } from '../components/ErrorBoundary.js';

// ===== Types =====

interface Sequence {
  id: string;
  name: string;
  triggerType: string;
  programFilter: string | null;
  active: boolean;
  createdAt: string;
}

interface Step {
  id: string;
  sequenceId: string;
  stepNumber: number;
  delayDays: number;
  delayHours: number;
  channel: string;
  templateId: string | null;
  subjectLine: string | null;
  active: boolean;
}

interface Template {
  id: string;
  name: string;
  subject: string;
  bodyHtml?: string;
  category: string | null;
  createdAt: string;
  updatedAt: string;
}

interface Execution {
  id: string;
  leadId: string;
  sequenceId: string;
  currentStep: number;
  status: string;
  enrolledAt: string;
  nextStepDueAt: string | null;
  lastStepSentAt: string | null;
  leadName: string;
  leadEmail: string;
  sequenceName: string;
}

interface Lead {
  id: string;
  name: string;
  email: string;
}

type DripTab = 'sequences' | 'templates' | 'activity';

const TABS: { key: DripTab; label: string }[] = [
  { key: 'sequences', label: 'Sequences' },
  { key: 'templates', label: 'Templates' },
  { key: 'activity', label: 'Activity' },
];

// ===== Main Page =====

export function DripPage() {
  const [tab, setTab] = useState<DripTab>('sequences');
  const { orgId: authOrgId } = useAuth();
  const { viewingOrgId, userRole } = useOrg();
  const orgId = viewingOrgId ?? authOrgId;
  const { toast } = useToast();
  const isAdmin = userRole === 'admin' || userRole === 'super_admin' || userRole === 'org_owner';

  if (!orgId) {
    return (
      <div className="text-center py-16">
        <p className="text-gray-500 text-sm">Select an account to manage drip campaigns.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Drip Campaigns</h2>
        <p className="text-sm text-gray-500">Automated follow-up sequences for leads</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex-1 py-2 text-xs font-medium rounded-md transition-colors ${
              tab === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'sequences' && <ErrorBoundary><SequencesTab key={orgId} orgId={orgId} isAdmin={isAdmin} /></ErrorBoundary>}
      {tab === 'templates' && <ErrorBoundary><TemplatesTab key={orgId} orgId={orgId} isAdmin={isAdmin} /></ErrorBoundary>}
      {tab === 'activity' && <ErrorBoundary><ActivityTab key={orgId} orgId={orgId} isAdmin={isAdmin} /></ErrorBoundary>}
    </div>
  );
}

// ===== Sequences Tab =====

function SequencesTab({ orgId, isAdmin }: { orgId: string; isAdmin: boolean }) {
  const { toast } = useToast();
  const [sequences, setSequences] = useState<Sequence[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [expandedSeqId, setExpandedSeqId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Sequence | null>(null);
  const [fetchError, setFetchError] = useState(false);

  const fetchSequences = useCallback(async () => {
    setIsLoading(true);
    setFetchError(false);
    try {
      const [seqData, tmplData] = await Promise.all([
        apiFetch<{ sequences: Sequence[] }>(`/orgs/${orgId}/drip/sequences`),
        apiFetch<{ templates: Template[] }>(`/orgs/${orgId}/drip/templates`),
      ]);
      setSequences(seqData.sequences);
      setTemplates(tmplData.templates);
    } catch { setFetchError(true); }
    finally { setIsLoading(false); }
  }, [orgId]);

  useEffect(() => { fetchSequences(); }, [fetchSequences]);

  const handleCreate = async (name: string, programFilter: string) => {
    try {
      await apiFetch(`/orgs/${orgId}/drip/sequences`, {
        method: 'POST',
        body: JSON.stringify({ name, trigger_type: 'lead.created', program_filter: programFilter || undefined }),
      });
      toast('success', `Sequence "${name}" created`);
      setShowCreate(false);
      fetchSequences();
    } catch { toast('error', 'Failed to create sequence'); }
  };

  const handleToggle = async (seq: Sequence) => {
    // Optimistic update
    setSequences(prev => prev.map(s => s.id === seq.id ? { ...s, active: !s.active } : s));
    try {
      await apiFetch(`/orgs/${orgId}/drip/sequences/${seq.id}`, {
        method: 'PUT', body: JSON.stringify({ active: !seq.active }),
      });
    } catch {
      setSequences(prev => prev.map(s => s.id === seq.id ? { ...s, active: seq.active } : s));
      toast('error', 'Failed to update');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await apiFetch(`/orgs/${orgId}/drip/sequences/${id}`, { method: 'DELETE' });
      toast('info', 'Sequence deleted');
      setConfirmDelete(null);
      fetchSequences();
    } catch { toast('error', 'Failed to delete'); }
  };

  return (
    <div className="space-y-3">
      {isAdmin && (
        <div className="flex justify-end">
          <button onClick={() => setShowCreate(!showCreate)}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700">
            + New Sequence
          </button>
        </div>
      )}

      {showCreate && <CreateSequenceForm onCreate={handleCreate} onCancel={() => setShowCreate(false)} />}

      {isLoading ? (
        <div className="space-y-3">{[1,2].map(i => <div key={i} className="h-20 bg-gray-100 rounded-xl animate-pulse" />)}</div>
      ) : fetchError ? (
        <div className="text-center py-8">
          <p className="text-sm text-red-600 mb-2">Failed to load sequences</p>
          <button onClick={() => fetchSequences()} className="text-sm text-primary-600 hover:text-primary-800 font-medium">Try again</button>
        </div>
      ) : sequences.length === 0 ? (
        <EmptyState icon="mail" title="No drip sequences yet" subtitle="Create a sequence to automatically follow up with new leads." />
      ) : (
        <div className="space-y-2">
          {sequences.map(seq => (
            <div key={seq.id}>
              <div className="bg-white rounded-xl border border-gray-100 p-4">
                <div className="flex items-center justify-between">
                  <button className="flex-1 text-left" onClick={() => setExpandedSeqId(expandedSeqId === seq.id ? null : seq.id)}>
                    <div className="flex items-center gap-2">
                      <svg className={`w-4 h-4 text-gray-400 transition-transform ${expandedSeqId === seq.id ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      <p className="font-medium text-gray-900 text-sm">{seq.name}</p>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${seq.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                        {seq.active ? 'Active' : 'Paused'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 ml-6">
                      Trigger: {seq.triggerType}{seq.programFilter ? ` (${seq.programFilter})` : ''}
                    </p>
                  </button>
                  {isAdmin && (
                    <div className="flex items-center gap-2">
                      <button onClick={() => handleToggle(seq)} className="text-xs text-primary-600 hover:text-primary-800 font-medium">
                        {seq.active ? 'Pause' : 'Activate'}
                      </button>
                      <button onClick={() => setConfirmDelete(seq)} className="text-xs text-red-500 hover:text-red-700">Delete</button>
                    </div>
                  )}
                </div>
              </div>
              {expandedSeqId === seq.id && (
                <div className="ml-4 mt-1">
                  <SequenceDetail orgId={orgId} sequence={seq} isAdmin={isAdmin} templates={templates} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {confirmDelete && (
        <ConfirmDialog
          title={`Delete "${confirmDelete.name}"?`}
          description="This will permanently delete all steps. Active enrollments may be affected."
          confirmLabel="Delete Sequence"
          onConfirm={() => handleDelete(confirmDelete.id)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}

// ===== Sequence Detail (Steps + Enroll) =====

function SequenceDetail({ orgId, sequence, isAdmin, templates }: { orgId: string; sequence: Sequence; isAdmin: boolean; templates: Template[] }) {
  const { toast } = useToast();
  const [steps, setSteps] = useState<Step[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddStep, setShowAddStep] = useState(false);
  const [editingStepId, setEditingStepId] = useState<string | null>(null);
  const [showEnroll, setShowEnroll] = useState(false);

  const fetchSteps = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await apiFetch<{ steps: Step[] }>(`/orgs/${orgId}/drip/sequences/${sequence.id}/steps`);
      setSteps(data.steps);
    } catch { toast('error', 'Failed to load steps'); }
    finally { setIsLoading(false); }
  }, [orgId, sequence.id, toast]);

  useEffect(() => { fetchSteps(); }, [fetchSteps]);

  const handleAddStep = async (data: { delay_days: number; delay_hours: number; channel: string; template_id: string | null; subject_line: string | null }) => {
    try {
      await apiFetch(`/orgs/${orgId}/drip/sequences/${sequence.id}/steps`, {
        method: 'POST',
        body: JSON.stringify({ step_number: steps.length + 1, ...data }),
      });
      toast('success', 'Step added');
      setShowAddStep(false);
      fetchSteps();
    } catch { toast('error', 'Failed to add step'); }
  };

  const handleUpdateStep = async (stepId: string, data: Record<string, unknown>) => {
    try {
      await apiFetch(`/orgs/${orgId}/drip/sequences/${sequence.id}/steps/${stepId}`, {
        method: 'PUT', body: JSON.stringify(data),
      });
      toast('success', 'Step updated');
      setEditingStepId(null);
      fetchSteps();
    } catch { toast('error', 'Failed to update step'); }
  };

  const handleDeleteStep = async (stepId: string) => {
    try {
      await apiFetch(`/orgs/${orgId}/drip/sequences/${sequence.id}/steps/${stepId}`, { method: 'DELETE' });
      toast('info', 'Step removed');
      fetchSteps();
    } catch (err) {
      const msg = err instanceof ApiError && err.code === 'has_active_executions'
        ? 'Cannot delete — a lead is about to receive this step'
        : 'Failed to delete step';
      toast('error', msg);
    }
  };

  const [isReordering, setIsReordering] = useState(false);
  const handleMoveStep = async (stepId: string, direction: 'up' | 'down') => {
    if (isReordering) return;
    const idx = steps.findIndex(s => s.id === stepId);
    if (idx < 0) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= steps.length) return;
    const step = steps[idx]!;
    const swapStep = steps[swapIdx]!;
    // Optimistic update
    const newSteps = [...steps];
    newSteps[idx] = { ...step, stepNumber: swapStep.stepNumber };
    newSteps[swapIdx] = { ...swapStep, stepNumber: step.stepNumber };
    newSteps.sort((a, b) => a.stepNumber - b.stepNumber);
    setSteps(newSteps);
    setIsReordering(true);
    try {
      // Sequential to avoid race condition
      await apiFetch(`/orgs/${orgId}/drip/sequences/${sequence.id}/steps/${step.id}`, {
        method: 'PUT', body: JSON.stringify({ step_number: swapStep.stepNumber }),
      });
      await apiFetch(`/orgs/${orgId}/drip/sequences/${sequence.id}/steps/${swapStep.id}`, {
        method: 'PUT', body: JSON.stringify({ step_number: step.stepNumber }),
      });
    } catch {
      toast('error', 'Failed to reorder');
      fetchSteps(); // Revert to server state
    } finally { setIsReordering(false); }
  };

  if (isLoading) {
    return <div className="space-y-2 py-2">{[1,2].map(i => <div key={i} className="h-12 bg-gray-50 rounded-lg animate-pulse" />)}</div>;
  }

  return (
    <div className="space-y-3 py-2">
      {/* Steps */}
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Steps ({steps.length})</p>
        <div className="flex gap-2">
          {isAdmin && (
            <>
              <button onClick={() => setShowEnroll(!showEnroll)} className="text-xs text-primary-600 hover:text-primary-800 font-medium">
                {showEnroll ? 'Close' : 'Enroll Lead'}
              </button>
              <button onClick={() => setShowAddStep(!showAddStep)} className="text-xs text-primary-600 hover:text-primary-800 font-medium">
                + Add Step
              </button>
            </>
          )}
        </div>
      </div>

      {showEnroll && <EnrollForm orgId={orgId} sequenceId={sequence.id} onDone={() => setShowEnroll(false)} />}

      {steps.length === 0 ? (
        <p className="text-xs text-gray-400 py-2">No steps yet. Add a step to define the sequence flow.</p>
      ) : (
        <div className="space-y-1">
          {steps.map((step, idx) => (
            <div key={step.id} className="bg-gray-50 rounded-lg p-3">
              {editingStepId === step.id ? (
                <StepForm
                  initial={step}
                  templates={templates}
                  onSave={(data) => handleUpdateStep(step.id, data)}
                  onCancel={() => setEditingStepId(null)}
                />
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-xs font-bold">{step.stepNumber}</span>
                    <div>
                      <p className="text-sm text-gray-900">
                        {step.delayDays > 0 && `${step.delayDays}d `}{step.delayHours > 0 && `${step.delayHours}h `}
                        {step.delayDays === 0 && step.delayHours === 0 ? 'Immediately' : 'delay'} — {step.channel}
                      </p>
                      <p className="text-xs text-gray-500">
                        {step.subjectLine ?? (templates.find(t => t.id === step.templateId)?.name ?? 'No template')}
                      </p>
                    </div>
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-1">
                      {idx > 0 && (
                        <button onClick={() => handleMoveStep(step.id, 'up')} className="w-10 h-10 p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100" title="Move up">
                          <svg className="w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
                        </button>
                      )}
                      {idx < steps.length - 1 && (
                        <button onClick={() => handleMoveStep(step.id, 'down')} className="w-10 h-10 p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100" title="Move down">
                          <svg className="w-4 h-4 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                        </button>
                      )}
                      <button onClick={() => setEditingStepId(step.id)} className="text-xs text-primary-600 hover:text-primary-800 font-medium ml-2">Edit</button>
                      <button onClick={() => handleDeleteStep(step.id)} className="text-xs text-red-500 hover:text-red-700">Remove</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showAddStep && (
        <StepForm templates={templates} onSave={handleAddStep} onCancel={() => setShowAddStep(false)} />
      )}
    </div>
  );
}

// ===== Step Form =====

function StepForm({ initial, templates, onSave, onCancel }: {
  initial?: Step;
  templates: Template[];
  onSave: (data: Record<string, unknown>) => void;
  onCancel: () => void;
}) {
  const [delayDays, setDelayDays] = useState(initial?.delayDays ?? 1);
  const [delayHours, setDelayHours] = useState(initial?.delayHours ?? 0);
  const [channel, setChannel] = useState(initial?.channel ?? 'email');
  const [templateId, setTemplateId] = useState(initial?.templateId ?? '');
  const [subjectLine, setSubjectLine] = useState(initial?.subjectLine ?? '');

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-500">Delay (days)</label>
          <input type="number" min={0} value={delayDays} onChange={e => setDelayDays(Number(e.target.value))}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
        </div>
        <div>
          <label className="text-xs text-gray-500">Delay (hours)</label>
          <input type="number" min={0} max={23} value={delayHours} onChange={e => setDelayHours(Number(e.target.value))}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-500">Channel</label>
          <select value={channel} onChange={e => setChannel(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
            <option value="email">Email</option>
            <option value="sms">SMS</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500">Template</label>
          <select value={templateId} onChange={e => setTemplateId(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
            <option value="">None</option>
            {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="text-xs text-gray-500">Subject line override</label>
        <input value={subjectLine} onChange={e => setSubjectLine(e.target.value)} placeholder="Leave empty to use template subject"
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
      </div>
      <div className="flex gap-2">
        <button onClick={() => onSave({ delay_days: delayDays, delay_hours: delayHours, channel, template_id: templateId || null, subject_line: subjectLine || null })}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700">
          {initial ? 'Save' : 'Add Step'}
        </button>
        <button onClick={onCancel} className="text-sm text-gray-500 px-3 py-2">Cancel</button>
      </div>
    </div>
  );
}

// ===== Enroll Form =====

function EnrollForm({ orgId, sequenceId, onDone }: { orgId: string; sequenceId: string; onDone: () => void }) {
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<Lead[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (search.length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const data = await apiFetch<{ leads: Lead[] }>(`/orgs/${orgId}/leads?search=${encodeURIComponent(search)}&limit=10`);
        setResults(data.leads);
      } catch { /* */ }
      finally { setIsSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const handleEnroll = async (leadId: string, leadName: string) => {
    try {
      await apiFetch(`/orgs/${orgId}/drip/executions`, {
        method: 'POST', body: JSON.stringify({ lead_id: leadId, sequence_id: sequenceId }),
      });
      toast('success', `${leadName} enrolled`);
      onDone();
    } catch (err) {
      const msg = err instanceof ApiError && err.code === 'already_enrolled'
        ? 'Lead is already enrolled in this sequence'
        : 'Failed to enroll lead';
      toast('error', msg);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3 space-y-2">
      <p className="text-xs font-medium text-gray-700">Enroll a lead in this sequence</p>
      <input
        value={search} onChange={e => setSearch(e.target.value)}
        placeholder="Search leads by name or email..."
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        autoFocus
      />
      {isSearching && <p className="text-xs text-gray-400">Searching...</p>}
      {results.length > 0 && (
        <div className="max-h-40 overflow-y-auto space-y-1">
          {results.map(lead => (
            <button key={lead.id} onClick={() => handleEnroll(lead.id, lead.name)}
              className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 flex items-center justify-between text-sm">
              <div>
                <p className="text-gray-900">{lead.name}</p>
                <p className="text-xs text-gray-500">{lead.email}</p>
              </div>
              <span className="text-xs text-primary-600 font-medium">Enroll</span>
            </button>
          ))}
        </div>
      )}
      {search.length >= 2 && !isSearching && results.length === 0 && (
        <p className="text-xs text-gray-400">No leads found</p>
      )}
    </div>
  );
}

// ===== Templates Tab =====

function TemplatesTab({ orgId, isAdmin }: { orgId: string; isAdmin: boolean }) {
  const { toast } = useToast();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Template | null>(null);
  const [fetchError, setFetchError] = useState(false);

  const fetchTemplates = useCallback(async () => {
    setIsLoading(true);
    setFetchError(false);
    try {
      const data = await apiFetch<{ templates: Template[] }>(`/orgs/${orgId}/drip/templates`);
      setTemplates(data.templates);
    } catch { setFetchError(true); }
    finally { setIsLoading(false); }
  }, [orgId]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleCreate = async (data: { name: string; subject: string; body_html: string; category: string }) => {
    try {
      await apiFetch(`/orgs/${orgId}/drip/templates`, {
        method: 'POST', body: JSON.stringify(data),
      });
      toast('success', `Template "${data.name}" created`);
      setShowCreate(false);
      fetchTemplates();
    } catch { toast('error', 'Failed to create template'); }
  };

  const loadTemplate = async (tmplId: string) => {
    try {
      const tmpl = await apiFetch<Template>(`/orgs/${orgId}/drip/templates/${tmplId}`);
      setEditingTemplate(tmpl);
    } catch { toast('error', 'Failed to load template'); }
  };

  const handleUpdate = async (data: { name: string; subject: string; body_html: string; category: string }) => {
    if (!editingTemplate) return;
    try {
      await apiFetch(`/orgs/${orgId}/drip/templates/${editingTemplate.id}`, {
        method: 'PUT', body: JSON.stringify(data),
      });
      toast('success', 'Template saved');
      setEditingTemplate(null);
      fetchTemplates();
    } catch { toast('error', 'Failed to save template'); }
  };

  const handleDelete = async (id: string) => {
    try {
      await apiFetch(`/orgs/${orgId}/drip/templates/${id}`, { method: 'DELETE' });
      toast('info', 'Template deleted');
      setConfirmDelete(null);
      fetchTemplates();
    } catch (err) {
      const msg = err instanceof ApiError && err.code === 'template_in_use'
        ? 'Template is in use by one or more steps. Remove it from steps first.'
        : 'Failed to delete template';
      toast('error', msg);
    }
  };

  return (
    <div className="space-y-3">
      {isAdmin && (
        <div className="flex justify-end">
          <button onClick={() => { setShowCreate(!showCreate); setEditingTemplate(null); }}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700">
            + New Template
          </button>
        </div>
      )}

      {(showCreate || editingTemplate) && (
        <TemplateForm
          initial={editingTemplate ?? undefined}
          onSave={editingTemplate ? handleUpdate : handleCreate}
          onCancel={() => { setShowCreate(false); setEditingTemplate(null); }}
        />
      )}

      {isLoading ? (
        <div className="space-y-3">{[1,2].map(i => <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse" />)}</div>
      ) : fetchError ? (
        <div className="text-center py-8">
          <p className="text-sm text-red-600 mb-2">Failed to load templates</p>
          <button onClick={() => fetchTemplates()} className="text-sm text-primary-600 hover:text-primary-800 font-medium">Try again</button>
        </div>
      ) : templates.length === 0 ? (
        <EmptyState icon="doc" title="No templates yet" subtitle="Create email templates for your drip sequences." />
      ) : (
        <div className="space-y-2">
          {templates.map(tmpl => (
            <div key={tmpl.id} className="bg-white rounded-xl border border-gray-100 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900 text-sm">{tmpl.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">Subject: {tmpl.subject}</p>
                  {tmpl.category && <span className="text-xs px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 font-medium mt-1 inline-block">{tmpl.category}</span>}
                </div>
                {isAdmin && (
                  <div className="flex items-center gap-2">
                    <button onClick={() => loadTemplate(tmpl.id)} className="text-xs text-primary-600 hover:text-primary-800 font-medium">Edit</button>
                    <button onClick={() => setConfirmDelete(tmpl)} className="text-xs text-red-500 hover:text-red-700">Delete</button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {confirmDelete && (
        <ConfirmDialog
          title={`Delete "${confirmDelete.name}"?`}
          description="This template will be permanently deleted. It cannot be in use by any steps."
          confirmLabel="Delete Template"
          onConfirm={() => handleDelete(confirmDelete.id)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}

// ===== Template Form =====

function TemplateForm({ initial, onSave, onCancel }: {
  initial?: Template;
  onSave: (data: { name: string; subject: string; body_html: string; category: string }) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [subject, setSubject] = useState(initial?.subject ?? '');
  const [bodyHtml, setBodyHtml] = useState(initial?.bodyHtml ?? '');
  const [category, setCategory] = useState(initial?.category ?? 'nurture');
  const [showPreview, setShowPreview] = useState(false);

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
      <h3 className="font-medium text-gray-900 text-sm">{initial ? 'Edit Template' : 'New Template'}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-500">Name</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Welcome Email"
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
        </div>
        <div>
          <label className="text-xs text-gray-500">Category</label>
          <select value={category} onChange={e => setCategory(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
            <option value="nurture">Nurture</option>
            <option value="welcome">Welcome</option>
            <option value="follow-up">Follow-up</option>
            <option value="promo">Promotional</option>
          </select>
        </div>
      </div>
      <div>
        <label className="text-xs text-gray-500">Subject</label>
        <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Email subject line"
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
      </div>
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs text-gray-500">Body (HTML)</label>
          <button onClick={() => setShowPreview(!showPreview)} className="text-xs text-primary-600 hover:text-primary-800 font-medium">
            {showPreview ? 'Edit' : 'Preview'}
          </button>
        </div>
        {showPreview ? (
          <iframe
            srcDoc={bodyHtml || '<p style="color:#999;font-family:sans-serif">No content yet</p>'}
            sandbox=""
            title="Template preview"
            className="w-full h-48 border border-gray-200 rounded-lg bg-white"
          />
        ) : (
          <textarea value={bodyHtml} onChange={e => setBodyHtml(e.target.value)} rows={8}
            placeholder="<p>Hi {{lead.name}},</p><p>Welcome to...</p>"
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500" />
        )}
      </div>
      <div className="flex gap-2">
        <button onClick={() => onSave({ name, subject, body_html: bodyHtml, category })}
          disabled={!name || !subject || !bodyHtml}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50">
          {initial ? 'Save' : 'Create Template'}
        </button>
        <button onClick={onCancel} className="text-sm text-gray-500 px-3 py-2">Cancel</button>
      </div>
    </div>
  );
}

// ===== Activity Tab =====

function ActivityTab({ orgId, isAdmin }: { orgId: string; isAdmin: boolean }) {
  const { toast } = useToast();
  const [executions, setExecutions] = useState<Execution[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState<Execution | null>(null);

  const fetchExecutions = useCallback(async (cursor?: string) => {
    if (!cursor) { setIsLoading(true); setFetchError(false); }
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (cursor) params.set('cursor', cursor);
      params.set('limit', '50');
      const data = await apiFetch<{ executions: Execution[]; next_cursor: string | null }>(
        `/orgs/${orgId}/drip/executions?${params.toString()}`
      );
      if (cursor) {
        setExecutions(prev => [...prev, ...data.executions]);
      } else {
        setExecutions(data.executions);
      }
      setNextCursor(data.next_cursor);
    } catch { if (!cursor) setFetchError(true); }
    finally { setIsLoading(false); }
  }, [orgId, statusFilter]);

  useEffect(() => { fetchExecutions(); }, [fetchExecutions]);

  const handleStatusChange = async (execId: string, newStatus: string) => {
    try {
      await apiFetch(`/orgs/${orgId}/drip/executions/${execId}`, {
        method: 'PUT', body: JSON.stringify({ status: newStatus }),
      });
      toast('success', `Enrollment ${newStatus}`);
      fetchExecutions();
    } catch { toast('error', 'Failed to update'); }
  };

  const statusColors: Record<string, string> = {
    active: 'bg-green-100 text-green-700',
    completed: 'bg-blue-100 text-blue-700',
    paused: 'bg-yellow-100 text-yellow-700',
    cancelled: 'bg-gray-100 text-gray-600',
    opted_out: 'bg-red-100 text-red-700',
  };

  return (
    <div className="space-y-3">
      {/* Filter */}
      <div className="flex gap-2 flex-wrap">
        {['', 'active', 'completed', 'paused', 'cancelled'].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${
              statusFilter === s ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-500 hover:text-gray-700'
            }`}>
            {s || 'All'}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-14 bg-gray-100 rounded-xl animate-pulse" />)}</div>
      ) : fetchError ? (
        <div className="text-center py-8">
          <p className="text-sm text-red-600 mb-2">Failed to load activity</p>
          <button onClick={() => fetchExecutions()} className="text-sm text-primary-600 hover:text-primary-800 font-medium">Try again</button>
        </div>
      ) : executions.length === 0 ? (
        <EmptyState icon="activity" title="No activity yet" subtitle="Enrollments will appear here when leads enter drip sequences." />
      ) : (
        <div className="space-y-2">
          {executions.map(exec => (
            <div key={exec.id} className="bg-white rounded-xl border border-gray-100 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-gray-900">{exec.leadName}</p>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${statusColors[exec.status] ?? 'bg-gray-100 text-gray-600'}`}>
                      {exec.status}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {exec.sequenceName} — Step {exec.currentStep}
                    {exec.nextStepDueAt && exec.status === 'active' && ` — Next: ${new Date(exec.nextStepDueAt).toLocaleDateString()}`}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {exec.leadEmail} — Enrolled {new Date(exec.enrolledAt).toLocaleDateString()}
                  </p>
                </div>
                {isAdmin && exec.status === 'active' && (
                  <div className="flex items-center gap-2">
                    <button onClick={() => handleStatusChange(exec.id, 'paused')} className="text-xs text-yellow-600 hover:text-yellow-800 font-medium">Pause</button>
                    <button onClick={() => setConfirmCancel(exec)} className="text-xs text-red-500 hover:text-red-700">Cancel</button>
                  </div>
                )}
                {isAdmin && exec.status === 'paused' && (
                  <button onClick={() => handleStatusChange(exec.id, 'active')} className="text-xs text-green-600 hover:text-green-800 font-medium">Resume</button>
                )}
              </div>
            </div>
          ))}

          {nextCursor && (
            <button onClick={() => fetchExecutions(nextCursor)}
              className="w-full py-2 text-sm text-primary-600 hover:text-primary-800 font-medium">
              Load more
            </button>
          )}
        </div>
      )}

      {confirmCancel && (
        <ConfirmDialog
          title={`Cancel enrollment for ${confirmCancel.leadName}?`}
          description={`This will stop the "${confirmCancel.sequenceName}" sequence for this lead. They will not receive any further emails.`}
          confirmLabel="Cancel Enrollment"
          onConfirm={() => { handleStatusChange(confirmCancel.id, 'cancelled'); setConfirmCancel(null); }}
          onCancel={() => setConfirmCancel(null)}
        />
      )}
    </div>
  );
}

// ===== Shared Components =====

function CreateSequenceForm({ onCreate, onCancel }: { onCreate: (name: string, filter: string) => void; onCancel: () => void }) {
  const [name, setName] = useState('');
  const [filter, setFilter] = useState('');

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
      <h3 className="font-medium text-gray-900 text-sm">New Drip Sequence</h3>
      <input placeholder="Sequence name (e.g., Welcome Series)" value={name} onChange={e => setName(e.target.value)}
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
      <input placeholder="Program filter (optional, e.g., kids_ma)" value={filter} onChange={e => setFilter(e.target.value)}
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
      <p className="text-xs text-gray-400">Leave filter empty to enroll all new leads. Set a tag to only enroll leads with that tag.</p>
      <div className="flex gap-2">
        <button onClick={() => onCreate(name, filter)} disabled={!name}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50">Create</button>
        <button onClick={onCancel} className="text-sm text-gray-500 px-3 py-2">Cancel</button>
      </div>
    </div>
  );
}

function ConfirmDialog({ title, description, confirmLabel, onConfirm, onCancel }: {
  title: string; description: string; confirmLabel: string;
  onConfirm: () => void; onCancel: () => void;
}) {
  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-50" onClick={onCancel} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6 space-y-4">
          <h3 className="font-semibold text-gray-900">{title}</h3>
          <p className="text-sm text-gray-500">{description}</p>
          <div className="flex gap-2 justify-end">
            <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg">Cancel</button>
            <button onClick={onConfirm} className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium">{confirmLabel}</button>
          </div>
        </div>
      </div>
    </>
  );
}

function EmptyState({ icon, title, subtitle }: { icon: string; title: string; subtitle: string }) {
  const paths: Record<string, string> = {
    mail: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
    doc: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    activity: 'M13 10V3L4 14h7v7l9-11h-7z',
  };
  return (
    <div className="text-center py-16">
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={paths[icon] ?? paths['mail']!} />
        </svg>
      </div>
      <h3 className="text-gray-900 font-medium">{title}</h3>
      <p className="text-gray-500 text-sm mt-1">{subtitle}</p>
    </div>
  );
}
